// src/hooks/usePublishWorkflow.js
import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCarePlanInstance,
  publishCarePlan,
} from "../redux/actions/careplanActions";
import { checkLockStatus } from "../services/dashboardServices";

export function usePublishWorkflow() {
  const dispatch = useDispatch();
  const userProfile = useSelector((s) => s.auth.userProfile);

  // modal states
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [isImportantInfoEmpty, setIsImportantInfoEmpty] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockedBy, setLockedBy] = useState(null);

  // 1️⃣ validation logic
  const validate = useCallback(
    async (carePlanId) => {
      setShowValidationModal(false);
      setIsImportantInfoEmpty(false);

      const { payload: instance } = await dispatch(
        fetchCarePlanInstance(carePlanId)
      );

      const hasEmpty = instance.sections
        .filter((s) => s.id !== 1)
        .some((s) => s.items.some((i) => !i.answer?.trim()));

      if (hasEmpty) {
        setShowValidationModal(true);
        return false;
      }
      if (!instance.important_info?.trim()) {
        setIsImportantInfoEmpty(true);
        setShowValidationModal(true);
        return false;
      }
      return true;
    },
    [dispatch]
  );

  // 2️⃣ check lock‐status → validate → confirm
  const handlePublish = useCallback(
    async (carePlanId) => {
      try {
        const status = await checkLockStatus(carePlanId);
        if (status.is_locked && status.locked_by.id !== userProfile.id) {
          // if already locked by another user, show lock modal
          setLockedBy(status.locked_by.name);
          setShowLockModal(true);
          return;
        }
        if (await validate(carePlanId)) {
          setShowConfirm(true);
        }
      } catch (error) {
        console.error("Error checking lock status:", error);
        // handle error (e.g., show notification)
      }
    },
    [userProfile, validate]
  );

  // 3️⃣ actual publish
  const confirmPublish = useCallback(
    ({ carePlanId, serviceUserName, onSuccess, onError }) => {
      if (!carePlanId) return;
      dispatch(publishCarePlan({ carePlanId, serviceUserName }))
        .then(() => {
          setShowConfirm(false);
          onSuccess?.();
        })
        .catch((err) => {
          setShowConfirm(false);
          onError?.(err);
        });
    },
    [dispatch]
  );

  // 4️⃣ cancel (just close modal—no unlock needed)
  const cancelPublish = useCallback(() => {
    // hide all modals and clear lock state
    setShowConfirm(false);
    setShowLockModal(false);
    setLockedBy(null);
  }, []);

  return {
    // for rendering
    showValidationModal,
    isImportantInfoEmpty,
    showConfirm,
    showLockModal,
    lockedBy,

    // setters
    setShowValidationModal,
    setShowConfirm,
    setShowLockModal,
    setLockedBy,

    // actions
    handlePublish,
    confirmPublish,
    cancelPublish,
  };
}
